import type {
  SourceCadenceObservation, SourceCadenceReadContext, SourceCadenceReader,
} from '../streamRecovery/sourceCadenceContracts';

type SourceSample = {
  outboundId: string;
  captureGeneration: number;
  sourceId: string;
  timestamp: number;
  frames: number;
};

const nonnegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;
const counter = (value: unknown): value is number => nonnegative(value) && Number.isSafeInteger(value);

/** Samples actual input cadence, without substituting encoder output or configured FPS. */
export class SourceCadenceSampler implements SourceCadenceReader {
  private previous?: SourceSample;
  private sequence = 0;

  public observe(report: RTCStatsReport, context: SourceCadenceReadContext): SourceCadenceObservation | undefined {
    const current = this.readSource(report, context);
    const previous = this.previous;
    if (!current) {
      this.previous = undefined;
      return undefined;
    }
    if (previous && this.sameSource(previous, current) && current.timestamp === previous.timestamp) {
      // A duplicated stats result is not a new counter observation, even if polled again.
      if (current.frames !== previous.frames) this.previous = undefined;
      return undefined;
    }
    this.previous = current;
    if (!previous || !this.sameSource(previous, current)) return undefined;
    const intervalMs = current.timestamp - previous.timestamp;
    if (intervalMs <= 0 || intervalMs > 3_000 || current.frames < previous.frames) return undefined;
    return {
      captureGeneration: current.captureGeneration,
      sourceId: current.sourceId,
      sequence: ++this.sequence,
      sampledAtMs: context.sampledAtMs,
      intervalMs,
      framesDelta: current.frames - previous.frames,
    };
  }

  public reset(): void {
    this.previous = undefined;
    this.sequence = 0;
  }

  private readSource(report: RTCStatsReport, context: SourceCadenceReadContext): SourceSample | undefined {
    if (!counter(context.captureGeneration) || !nonnegative(context.sampledAtMs)) return undefined;
    const outbound = this.uniqueStat(report, context.outboundId);
    if (outbound?.['type'] !== 'outbound-rtp' || outbound['kind'] !== 'video'
      || typeof outbound['mediaSourceId'] !== 'string' || !outbound['mediaSourceId']) return undefined;
    const source = this.uniqueStat(report, outbound['mediaSourceId']);
    if (source?.['type'] !== 'media-source' || source['kind'] !== 'video'
      || source['id'] !== outbound['mediaSourceId'] || typeof source['id'] !== 'string' || !counter(source['frames'])
      || !nonnegative(source['timestamp'])) return undefined;
    return {
      outboundId: context.outboundId,
      captureGeneration: context.captureGeneration,
      sourceId: source['id'],
      timestamp: source['timestamp'],
      frames: source['frames'],
    };
  }

  private uniqueStat(report: RTCStatsReport, id: string): Record<string, unknown> | undefined {
    const matches: Record<string, unknown>[] = [];
    report.forEach(stat => { if (stat.id === id) matches.push(stat); });
    return matches.length === 1 ? matches[0] : undefined;
  }

  private sameSource(left: SourceSample, right: SourceSample): boolean {
    return left.outboundId === right.outboundId && left.sourceId === right.sourceId
      && left.captureGeneration === right.captureGeneration;
  }
}
